import { AsyncPipe, NgClass, NgIf, TitleCasePipe } from '@angular/common';
import {
	AfterViewChecked,
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink, UrlSegment } from '@angular/router';
import { BannerTarget, DynamicContentService, DynamicContentTargets } from '@woolworthsnz/dynamic-content';
import { distinctUntilFulfilmentChanged, FulfilmentStoreService } from '@woolworthsnz/fulfilment';
import {
	FilterChangeArgs,
	DasFilterChangeArgs,
	InfiniteScrollingService,
	NoResultsComponent,
	PiesSearchHeaderComponent,
	ProductSearchResponse,
	SearchCategoryListComponent,
	SearchDasFilterComponent,
	SearchFooterComponent,
	SearchMediatorService,
	SearchQueryParams,
	SearchResultsHeaderComponent,
	SearchState,
	SearchFilterComboComponent,
	SearchErrorComponent,
} from '@woolworthsnz/search';
import {
	AppSettingsService,
	BoughtBeforeInSpcialsExperimentVariants,
	BreakPointService,
	ButtonComponent,
	DatalayerService,
	Device,
	FeatureService,
	FlagKey,
	FlagService,
	InfiniteLoadMoreExperimentVariants,
	isTheFirstPage,
	LoadMoreComponent,
	MedalliaSurveyDataPushService,
	MetaDataService,
	NotificationType,
	replaceSpecialCharacters,
	ScrollIntoViewDirective,
	SearchTarget,
	sentenceCase,
	ShopperService,
	SiteManagementService,
	SvgIconComponent,
	WithPerformanceTracking,
	NewLayoutOfProductCategoriesAndFiltersVariants,
	NewLayoutOfProductCategoriesAndFiltersExp2Variants,
	NewLayoutOfProductCategoriesAndFiltersVariables,
	WINDOW,
	CustomWindow,
} from '@woolworthsnz/styleguide';
import {
	BoostOfferProductTag,
	ContextResponse,
	DynamicContentRequest,
	FilterCriteria,
	ProductResponse,
} from '@woolworthsnz/trader-api';
import { BehaviorSubject, Observable, of, throwError, forkJoin, Subject } from 'rxjs';
import { catchError, map, skip, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { boostShopRangeDefaultTitle, SearchStoreService } from '../../search-store.service';
import { SearchService } from '../../search.service';
import { BreadCrumbService } from '../../../core/services/breadcrumb.service';
import { BrandSuggestionComponent, GridComponent } from '@woolworthsnz/product';
import {
	ALLOW_BOUGHT_BEFORE_ITEM_PAGES,
	MAX_BOUGHT_BEFORE_ITEM_POSITION,
	NO_BOUGHT_BEFORE_ITEM_POSITION,
} from './search.page.constants';
import { EDR_BOOST_OFFERS_PATH } from '@woolworthsnz/shop';
import { HttpParams } from '@angular/common/http';

export enum BrowseSection {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	Root,
	// eslint-disable-next-line @typescript-eslint/naming-convention
	SpecialsRoot,
}

enum DefaultVariation {
	default = 'default',
}

@WithPerformanceTracking('SearchPage', true)
@Component({
	selector: 'wnz-search',
	templateUrl: './search.page.html',
	styleUrls: ['./search.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		NgClass,
		ScrollIntoViewDirective,
		SearchResultsHeaderComponent,
		SearchDasFilterComponent,
		RouterLink,
		ButtonComponent,
		SvgIconComponent,
		PiesSearchHeaderComponent,
		GridComponent,
		BrandSuggestionComponent,
		SearchFooterComponent,
		NoResultsComponent,
		AsyncPipe,
		LoadMoreComponent,
		SearchCategoryListComponent,
		SearchFilterComboComponent,
		SearchErrorComponent,
	],
})
export class SearchPage implements OnInit, AfterViewInit, OnDestroy, AfterViewChecked {
	@ViewChild('scrollAnchor') scrollAnchor: ElementRef<HTMLElement>;
	@ViewChild('piesHeader', { read: ElementRef }) piesHeaderRef: ElementRef;
	@ViewChild('dynamicContentPanel') dynamicContentPanel: ElementRef;
	@ViewChild('searchHeaderRef') searchHeader: SearchResultsHeaderComponent;

	searchState: SearchState;
	isShopper: boolean | undefined;
	isLoggedIn: boolean | undefined;
	device = Device;
	currentDevice: Device;
	isShowInStockItemsOnlyFeatureEnabled$: Observable<boolean>;
	isHideSwitchToggleForOOSEnabled: boolean;
	piesHeaderHeight = 0;
	mySearchQuery = '';
	isShowInStockItemsOnlyFeatureEnabled: boolean;
	isGreatToLowPriceEnabled: boolean;
	isCartologyGAMSessionGroup: boolean;
	inStockProductsOnlyFromRoute: boolean | undefined;
	productIndexOffset$: Observable<number>;
	pageSize$: Observable<number>;
	cppItemCount$: Observable<number>;
	boughtBeforeInSpecialsExperimentVariationKey$: Observable<string>;
	maxBoughtBeforePosition = 0;

	dasFacets: FilterCriteria[];
	dasFacetsRoute$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
	dasFacetsQueryParams$: BehaviorSubject<SearchQueryParams[]> = new BehaviorSubject<SearchQueryParams[]>([]);

	isInfiniteLoadMoreEnabled = false;
	boostOffersLink: string;

	categoriesAndFiltersLayoutVariant:
		| NewLayoutOfProductCategoriesAndFiltersVariants
		| NewLayoutOfProductCategoriesAndFiltersExp2Variants;
	categoriesAndFiltersLayoutTargets: string[] = [];

	private intersectionObserver: IntersectionObserver | undefined;
	private trackingAfterNextCheckCount = 0;
	private prevPath = '';
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		@Inject(WINDOW) public window: CustomWindow,
		private appSettingsService: AppSettingsService,
		public breakPointService: BreakPointService,
		public breadCrumbService: BreadCrumbService,
		private cdr: ChangeDetectorRef,
		protected datalayerService: DatalayerService,
		private flagService: FlagService,
		private featureService: FeatureService,
		private fulfilmentStoreService: FulfilmentStoreService,
		protected router: Router,
		private route: ActivatedRoute,
		public searchService: SearchService,
		public searchStoreService: SearchStoreService,
		public searchMediatorService: SearchMediatorService,
		private shopperService: ShopperService,
		private metaDataService: MetaDataService,
		private titleCasePipe: TitleCasePipe,
		private infiniteScrollingService: InfiniteScrollingService,
		private siteManagementService: SiteManagementService,
		private medalliaSurveyDataPushService: MedalliaSurveyDataPushService,
		private dynamicContentService: DynamicContentService
	) {
		// This is in the constructor as we need to use router events

		this.breakPointService.device$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((device) => (this.currentDevice = device));
	}

	@HostBinding('attr.is-mobile-app')
	get isEmbeddedInMobileApp(): boolean {
		return this.currentDevice === Device.MOBILE && this.appSettingsService.settings.isEmbeddedInApp;
	}

	@HostBinding('attr.layout-extended')
	get layoutExtended(): boolean {
		return this.shouldShowNewLayoutOfProductCategoriesAndFilters || this.isSearchTarget;
	}

	get shouldShowNewLayoutOfProductCategoriesAndFilters(): boolean {
		return (
			[
				NewLayoutOfProductCategoriesAndFiltersVariants.V3,
				NewLayoutOfProductCategoriesAndFiltersExp2Variants.Control,
				NewLayoutOfProductCategoriesAndFiltersExp2Variants.V1,
				NewLayoutOfProductCategoriesAndFiltersExp2Variants.V2,
			].includes(this.categoriesAndFiltersLayoutVariant) &&
			this.target !== null &&
			this.categoriesAndFiltersLayoutTargets.includes(this.target)
		);
	}

	get isSearchTarget(): boolean {
		return this.target === SearchTarget.Search;
	}

	get shouldShowNewSearchFilter(): boolean {
		return this.layoutExtended;
	}

	get isTargetBrowseOrSearch(): boolean {
		return this.target === SearchTarget.Browse || this.target === SearchTarget.Search;
	}

	get itemsPerPageCollection(): string[] {
		return this.appSettingsService.getSetting('ITEMS_PER_PAGE');
	}

	get minimumPageSize(): number {
		return this.appSettingsService.getSetting('MINIMUM_PAGE_SIZE');
	}

	get filterGroups(): FilterCriteria[][] {
		return this.searchStoreService.getFilterGroups();
	}

	get searchQuery(): string {
		return this.mySearchQuery;
	}

	get isNewPageLoading$(): Observable<boolean> {
		return this.searchStoreService.state$.pipe(map((state) => state.loading || false));
	}

	get products(): ProductResponse[] | undefined | null {
		if (!this.searchState || !this.searchState.products) {
			return null;
		}

		if (this.isInfiniteLoadMoreEnabled) {
			return this.searchState.cumulativeProducts?.items;
		}

		return this.searchState.products.items;
	}

	get department(): string | undefined {
		return this.searchStoreService.breadcrumb?.department?.name;
	}

	get aisle(): string | undefined {
		return this.searchStoreService.breadcrumb?.aisle?.name;
	}

	get shelf(): string | undefined {
		return this.searchStoreService.breadcrumb?.shelf?.name;
	}

	get target(): SearchTarget | null {
		return this.route.snapshot?.data['target'] || null;
	}

	get title(): string {
		let output = '';

		if (this.target === SearchTarget.BoostOffer && this.searchState?.boostOfferTag) {
			return this.searchState.boostOfferTag.description?.list ?? boostShopRangeDefaultTitle;
		}

		if (this.target === SearchTarget.Search && this.searchQuery) {
			output = `Results for "${this.searchQuery}"`;
		} else if (this.searchQuery && this.searchState?.dasFilter?.name) {
			// Only fires when route has been changed internally
			output = this.searchState.dasFilter.name;
		} else {
			// Here to show the title when we first land on a browse/special
			const breadcrumb = this.searchStoreService.breadcrumb;

			if (!breadcrumb) {
				return output;
			}

			output =
				breadcrumb.shelf?.name ||
				breadcrumb.aisle?.name ||
				breadcrumb.department?.name ||
				breadcrumb.productGroup?.name ||
				breadcrumb.dynamicGroup ||
				'';
		}

		// This title cases the title when on a specific browse / specials page (e.g. by clicking a browse link instead of a search) and when embedded
		// so that it looks nice in the native header (which uses the page title for its text)
		// Only done for those conditions as this same title is used for SEO page metadata
		return this.isBrowseOrSpecialsAndEmbedded()
			? this.titleCasePipe.transform(output.trim())
			: sentenceCase(output.trim());
	}

	get boostOfferTag(): BoostOfferProductTag | undefined {
		if (
			this.target === SearchTarget.BoostOffer &&
			this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.BoostShopRange)
		) {
			return this.searchState?.boostOfferTag;
		}

		return undefined;
	}

	get url(): UrlSegment[] {
		return this.route.snapshot?.url;
	}

	get isTargetSpecialsRoot(): boolean {
		return (
			this.target === SearchTarget.Specials &&
			this.route.snapshot.data['browseSection'] === BrowseSection.SpecialsRoot
		);
	}

	get layoutVariant():
		| NewLayoutOfProductCategoriesAndFiltersVariants
		| NewLayoutOfProductCategoriesAndFiltersExp2Variants
		| DefaultVariation.default {
		if (this.isSearchTarget) {
			return DefaultVariation.default;
		}
		return this.categoriesAndFiltersLayoutVariant;
	}

	get categories(): FilterCriteria[] {
		if (!this.isSearchTarget) {
			return [];
		}
		return (
			((this.searchState?.aisleFacets?.length || 0) >= 3
				? this.searchState.aisleFacets
				: this.searchState?.shelfFacets) || []
		);
	}

	get selectedCategories(): FilterCriteria[] {
		return this.isSearchTarget ? this.searchState.dasFilters || [] : [];
	}

	ngAfterViewChecked(): void {
		if (this.trackingAfterNextCheckCount === 1) {
			const customProperties: { [key: string]: any } = {
				search: this.searchState.search,
				searchItemCount: this.searchState.products?.items?.length,
			};

			// Only add the embedded flag if the customer is embedded
			if (this.appSettingsService.settings.isEmbeddedInApp) {
				customProperties.isEmbedded = true;
			}

			// This function is added by @WithPerformanceTracking with manualMode = true.
			// TS Decorators can't actually modify the class interface yet so we need to ignore here
			// @ts-ignore
			this.endPaintTracking(customProperties);

			this.trackingAfterNextCheckCount++;
		}

		// Set PIES header height which will be used to dynamically set the margin of the side nav for search DAS filters
		// Do this in setTimeout() to mark for check in the next check cycle
		if (this.piesHeaderRef?.nativeElement?.offsetHeight !== this.piesHeaderHeight) {
			setTimeout(() => this.setSideNavTopMargin());
		}
	}

	ngOnInit(): void {
		this.toggleFeatureShowInStockItemsOnly();

		this.isShowInStockItemsOnlyFeatureEnabled$.pipe(takeUntil(this.destroyed$)).subscribe((status) => {
			if (status && !!this.route.snapshot.queryParams?.inStockProductsOnly) {
				this.inStockProductsOnlyFromRoute = this.route.snapshot.queryParams?.inStockProductsOnly === 'true';
			}
		});

		this.setPageMeta();

		this.setProductIndexOffset();

		this.cppItemCount$ = this.searchStoreService.state$.pipe(map((state: SearchState) => state.cppItemCount ?? 0));

		this.searchStoreService.state$.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
			this.searchState = s;
			this.cdr.detectChanges();
		});

		// If this is the Boost Shop Range page and there were no products loaded, then redirect to the home page
		this.searchStoreService.state$.pipe(skip(1), take(1)).subscribe(() => {
			if (
				this.target === SearchTarget.BoostOffer &&
				this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.BoostShopRange) &&
				!this.products?.length
			) {
				this.router.navigateByUrl('/');
			}
		});

		this.shopperService.state$.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
			this.isShopper = s.isShopper;
			this.isLoggedIn = s.isLoggedIn;
		});

		this.flagService
			.getVariationKey(FlagKey.infiniteLoadMore)
			.pipe(
				takeUntil(this.destroyed$),
				map((variantKey) => InfiniteLoadMoreExperimentVariants.InfiniteList === variantKey)
			)
			.subscribe((isEnabled) => {
				this.isInfiniteLoadMoreEnabled = isEnabled;
			});

		this.featureService.state$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.isHideSwitchToggleForOOSEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.HideSwitchToggleForOOS
			);
			this.isGreatToLowPriceEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.GreatToLowPrice
			);
			this.isCartologyGAMSessionGroup = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.CartologyGAMSessionGroup
			);
		});

		this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.mySearchQuery = this.route.snapshot.queryParamMap.get('search') || '';
			this.cdr.markForCheck();
			this.medalliaSurveyDataPushService.refreshMedallia();
		});

		this.updateSearchResultsOnFulfilmentChange();

		this.boughtBeforeInSpecialsExperimentVariationKey$ = this.flagService.getVariationKey(
			FlagKey.boughtBeforeInSpecials
		);

		this.setMaxBoughtBeforePosition();

		this.subscribeToEvents();

		this.boostOffersLink = `/${EDR_BOOST_OFFERS_PATH}`;

		const newLayoutExperimentVariant$ = forkJoin([
			this.flagService.getVariationKey<NewLayoutOfProductCategoriesAndFiltersVariants>(
				FlagKey.newLayoutOfProductCategoriesAndFilters
			),
			this.flagService.getVariationKey<NewLayoutOfProductCategoriesAndFiltersExp2Variants>(
				FlagKey.newLayoutOfProductCategoriesAndFiltersExp2
			),
		]);

		newLayoutExperimentVariant$.pipe(takeUntil(this.destroyed$)).subscribe((variants) => {
			const activeVariant = variants.find(
				(variant) => variant !== NewLayoutOfProductCategoriesAndFiltersVariants.Off
			);

			if (activeVariant) {
				this.categoriesAndFiltersLayoutVariant = activeVariant;
			}
		});

		const newLayoutExperimentTargets$ = forkJoin([
			this.flagService.getVariables<NewLayoutOfProductCategoriesAndFiltersVariables>(
				FlagKey.newLayoutOfProductCategoriesAndFilters
			),
			this.flagService.getVariables<NewLayoutOfProductCategoriesAndFiltersVariables>(
				FlagKey.newLayoutOfProductCategoriesAndFiltersExp2
			),
		]);

		newLayoutExperimentTargets$.pipe(takeUntil(this.destroyed$)).subscribe((variablesList) => {
			const activeVariables = variablesList.find(({ searchTargets }) => !!searchTargets);
			if (activeVariables) {
				this.categoriesAndFiltersLayoutTargets = activeVariables.searchTargets.split(',');
			}
		});
	}

	ngAfterViewInit(): void {
		this.route.data.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
			this.searchStoreService.setStateFromServiceResponse(
				data.searchResults,
				data.searchResults?.params,
				data.searchResults?.routeUrl || ''
			);

			let currPath = data.target;

			if (this.route.snapshot.url?.length > 0) {
				currPath = `${currPath}/${this.route.snapshot.url.join('/')}`;
			}

			if (this.route.snapshot.queryParams) {
				const currParams = new HttpParams({ fromObject: this.route.snapshot.queryParams }).toString();
				currPath = `${currPath}?${currParams}`;
			}

			if (currPath !== this.prevPath && this.shouldInitDynamicContent()) {
				// In some scenario, the panel is not ready when initialize the dynamic content
				//   e.g., first navigate to subcategory, then click Back in browser
				//   when browser navigate back, the dc panel is not ready in DOM
				//   therefore it fails to render dynamic content in specific panel
				//
				// The solution is waiting for the next tick to wait the panel is ready
				setTimeout(() => this.initDynamicContent());
			}

			this.prevPath = currPath;

			setTimeout(() => {
				this.dasFacetsQueryParams$.next(this.generateDasFacetsQueryParams());
				this.dasFacetsRoute$.next(this.generateDasFacetsRoute());
			});
			this.setPageMeta();
			this.trackingAfterNextCheckCount++;
			this.setSideNavTopMargin();
			if (this.isShowInStockItemsOnlyFeatureEnabled === true && !this.isHideSwitchToggleForOOSEnabled) {
				const inStockToggle = this.searchStoreService.inStockProductsOnlyForSearchAndBrowse
					? 'enabled'
					: 'disabled';
				this.datalayerService.trackNotificationEventWithInStockToggle(
					NotificationType.Toggle,
					inStockToggle,
					'product_list',
					'toggle_status'
				);
			}

			if (this.scrollAnchor?.nativeElement) {
				this.setupInfiniteScroller(this.scrollAnchor.nativeElement);
			}
		});

		this.setPageMeta();

		this.dynamicContentService.setViewChildForOutlet(
			this.dynamicContentPanel,
			DynamicContentTargets.dynamicContentPanel
		);
	}

	ngOnDestroy(): void {
		// Clear the search state so previous results don't show up initially when revisiting the search page.
		this.destroyed$.next(true);
		this.destroyed$.complete();
		this.searchStoreService.resetState();
		this.intersectionObserver?.disconnect();

		this.dynamicContentService.clearViewChildForOutlet(DynamicContentTargets.dynamicContentPanel);
	}

	loadMore(): void {
		this.searchMediatorService.emitLoadMoreEvent();
	}

	toggleFeatureShowInStockItemsOnly(): void {
		this.isShowInStockItemsOnlyFeatureEnabled$ = this.featureService.state$.pipe(
			map(() => {
				this.isShowInStockItemsOnlyFeatureEnabled = this.featureService.isFeatureEnabled(
					ContextResponse.EnabledFeaturesEnum.SwitchToggleToShowInStockItemsOnly
				);
				return this.isShowInStockItemsOnlyFeatureEnabled;
			})
		);
	}

	getPageTitle(): string {
		if (this.target === SearchTarget.Browse && this.route.snapshot.data['browseSection'] === BrowseSection.Root) {
			return this.appSettingsService.getPageTitle('browse', this.title);
		}

		if (this.isTargetSpecialsRoot) {
			return this.appSettingsService.getPageTitle('specials', this.title);
		}

		if (this.target === SearchTarget.Search) {
			return this.appSettingsService.getPageTitle('search', this.title);
		}

		if (this.target === SearchTarget.Browse && this.appSettingsService.settings.isEmbeddedInApp) {
			return this.appSettingsService.getPageTitle('productCategory', this.title);
		}

		if (this.target === SearchTarget.DeliverySubscription) {
			return this.appSettingsService.getPageTitle('deliverySubscription', this.title);
		}

		if (
			this.isGreatToLowPriceEnabled &&
			this.target === SearchTarget.DynamicProductGroup &&
			this.title.toLowerCase() === 'great price'
		) {
			return this.appSettingsService.getPageTitle('productCategory', 'low price');
		}

		return this.appSettingsService.getPageTitle('productCategory', this.title.toLowerCase());
	}

	getMetaTitle(): string {
		if (this.target === SearchTarget.Search) {
			return this.appSettingsService.getMetaTitle('search', this.title);
		}

		if (
			this.isGreatToLowPriceEnabled &&
			this.target === SearchTarget.DynamicProductGroup &&
			this.title.toLowerCase() === 'great price'
		) {
			return this.appSettingsService.getMetaTitle('productCategory', 'low price');
		}

		return this.appSettingsService.getMetaTitle('productCategory', this.title.toLowerCase());
	}

	getMetaDescription(): string {
		if (this.target === SearchTarget.Search) {
			return this.appSettingsService.getMetaDescription('search', this.title);
		}

		if (
			this.isGreatToLowPriceEnabled &&
			this.target === SearchTarget.DynamicProductGroup &&
			this.title.toLowerCase() === 'great price'
		) {
			return this.appSettingsService.getMetaDescription('productCategory', 'low price');
		}

		return this.appSettingsService.getMetaDescription('productCategory', this.title.toLowerCase());
	}

	getUrl(): string {
		return this.appSettingsService.getMetaUrl('default');
	}

	getImage(): string {
		return this.appSettingsService.getMetaImage('default');
	}

	shouldInitDynamicContent(): boolean {
		return isTheFirstPage(this.router.url);
	}

	initDynamicContent(): void {
		const departmentId =
			this.searchStoreService.breadcrumb.department && this.searchStoreService.breadcrumb.department.value;
		const aisleId = this.searchStoreService.breadcrumb.aisle && this.searchStoreService.breadcrumb.aisle.value;
		const shelfId = this.searchStoreService.breadcrumb.shelf && this.searchStoreService.breadcrumb.shelf.value;
		const productGroupId =
			this.searchStoreService.breadcrumb.productGroup && this.searchStoreService.breadcrumb.productGroup.value;

		this.dynamicContentService.init(<BannerTarget>{
			searchString: this.searchQuery,
			departmentId,
			aisleId,
			shelfId,
			productGroupId,
			targetContext: DynamicContentRequest.TargetContextEnum.Search,
		});
	}

	onClearFilters = (): void => {
		this.searchStoreService.navigateOnFilterChange([], this.url);
	};

	onRouteChange = (queryParams: any, route?: string): void => {
		if (!route) {
			route = this.breadCrumbService.buildUrlPath({
				breadcrumb: this.searchStoreService.breadcrumb,
				urlSegments: this.url,
				target: this.target,
			});
		}
		this.infiniteScrollingService.resetNumberOfAutoScrollsCount(); // reset number of scrolls when rerouted
		this.router.navigate([route], {
			queryParams,
			queryParamsHandling: 'merge',
		});
	};

	subscribeToEvents(): void {
		this.searchMediatorService.filterSelection$.pipe(takeUntil(this.destroyed$)).subscribe((filters: any) => {
			this.searchStoreService.navigateOnFilterChange(filters, this.url);
		});

		this.searchMediatorService.pageSizeChange$.pipe(takeUntil(this.destroyed$)).subscribe((size: number) => {
			this.onRouteChange({
				page: 1,
				size,
				inStockProductsOnly: this.searchStoreService.inStockProductsOnlyForSearchAndBrowse,
			});
		});

		this.searchMediatorService.pageChange$.pipe(takeUntil(this.destroyed$)).subscribe((page: number) => {
			this.onRouteChange({
				page,
				inStockProductsOnly: this.searchStoreService.inStockProductsOnlyForSearchAndBrowse,
			});
		});

		this.searchMediatorService.sortChange$.pipe(takeUntil(this.destroyed$)).subscribe((sortOption: string) => {
			this.onRouteChange({
				page: 1,
				sort: sortOption,
				inStockProductsOnly: this.searchStoreService.inStockProductsOnlyForSearchAndBrowse,
			});
		});

		this.searchMediatorService.loadMore$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			const page = this.searchState.currentPage + 1;

			// this would load the products dynamically instead of changing routes
			if (this.isInfiniteLoadMoreEnabled) {
				this.loadNextPageDynamically(page);
				return;
			}

			this.onRouteChange({
				page,
				inStockProductsOnly: this.searchStoreService.inStockProductsOnlyForSearchAndBrowse,
			});
		});

		this.searchMediatorService.scrollToTop$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.searchStoreService.setState({ scrollToTop: true });

			// As no state change happens we need to manually reset the scrollTop
			// This shouldn't be necessary anywhere else. When the below ticket
			// gets implemented this can be removed
			// TODO: [POD-1782] - Automatically present a "Return to Top of Page" button when user scrolls long pages
			setTimeout(() => {
				this.searchStoreService.setState({ scrollToTop: false });
			}, 10);
		});

		this.searchMediatorService.removeFilter$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((filter: FilterCriteria) => {
				this.searchStoreService.handleFilterChange(filter, false, this.url);
			});
		this.searchMediatorService.removeAllFilters$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.searchStoreService.navigateOnFilterChange([], this.url);
		});

		this.searchMediatorService.inStockProductsOnlyToggle$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((toggleState: boolean) => {
				this.searchStoreService.setState({ inStockProductsOnly: toggleState });
				this.searchStoreService.handleInStockProductsOnlyToggleChange(this.url);
			});
	}

	onFilterChanged(args: FilterChangeArgs): void {
		this.searchStoreService.handleFilterChange(args.filter, args.isSelected, args.url);
	}

	onFiltersChanged(args: Array<FilterChangeArgs>): void {
		this.searchStoreService.handleFiltersChange(args);
	}

	onCategoriesChanged(args: Array<DasFilterChangeArgs>): void {
		this.searchStoreService.handleDasFiltersChange(args);
	}

	onFacetChanged(dasFilter: FilterCriteria): void {
		this.searchStoreService.setState({ dasFilter });

		const queryParams = this.searchStoreService.buildQuery();

		this.onRouteChange(
			queryParams,
			this.breadCrumbService.buildUrlPath({
				breadcrumb: this.searchStoreService.breadcrumb,
				urlSegments: this.url,
				target: this.target,
				dasFilterName: replaceSpecialCharacters(dasFilter.name || ''),
			})
		);
	}

	generateDasFacetsRoute(): string[] {
		if (!this.searchState?.dasFacets) {
			return [];
		}

		return this.searchState?.dasFacets.map((dasFilter) =>
			this.breadCrumbService.buildUrlPath({
				breadcrumb: this.searchStoreService.breadcrumb,
				urlSegments: this.url,
				target: this.target,
				dasFilterName: replaceSpecialCharacters(dasFilter.name || ''),
			})
		);
	}

	generateDasFacetsQueryParams(): SearchQueryParams[] {
		if (!this.searchState?.dasFacets) {
			return [];
		}

		return this.searchState?.dasFacets.map((dasFilter) => this.searchStoreService.buildQuery(dasFilter));
	}

	onRemoveFilter(filterCriteria: FilterCriteria): void {
		this.searchMediatorService.emitRemoveFilterEvent(filterCriteria);
	}

	onRemoveAllFilters(): void {
		this.searchMediatorService.emitRemoveAllFiltersEvent();
	}

	onSortChanged(sortOption: string): void {
		this.searchMediatorService.emitSortChangeEvent(sortOption);
	}

	onInStockProductsOnlyToggleChange(toggleState: boolean): void {
		this.searchMediatorService.emitInStockProductsOnlyToggleEvent(toggleState);
	}

	private loadNextPageDynamically(page: number): void {
		this.searchService
			.fetchProductsFromRoute(this.route.snapshot, this.router.routerState.snapshot, {
				page,
				inStockProductsOnly: this.searchStoreService.inStockProductsOnlyForSearchAndBrowse,
			})
			.pipe(takeUntil(this.destroyed$))
			.subscribe((data: ProductSearchResponse) =>
				this.searchStoreService.setStateFromServiceResponse(data, data.params, '', true)
			);
	}

	private setupInfiniteScroller(element: HTMLElement): void {
		if (!this.isInfiniteLoadMoreEnabled) {
			return;
		}

		this.intersectionObserver = this.infiniteScrollingService.setupInfiniteScroller(element, () =>
			this.shouldLoadMore()
		);
	}

	private shouldLoadMore(): boolean {
		// check that we have a next page to load
		const totalItems = this.searchState.products?.totalItems || 0;
		const itemsCount = this.searchState.products?.items?.length || 0;

		return itemsCount >= this.minimumPageSize && totalItems > this.minimumPageSize;
	}

	/**
	 * Set dynamic page metadata based on the current context of the search page
	 * @private
	 */
	private setPageMeta(): void {
		if (this.isTargetSpecialsRoot) {
			this.setSpecialPageMeta();
		} else {
			this.metaDataService.setPageMeta(
				this.getPageTitle(),
				this.getMetaTitle(),
				this.getMetaDescription(),
				this.getImage(),
				this.getUrl()
			);
		}
	}

	private setSpecialPageMeta(): void {
		// The specials page (/shop/specials) is using the search page to list the configured specials.
		// The search results page metadata is typically dynamic based on the context, but for the 'specials' page the SEO
		// metadata should be treated as a static content page where the content team can configure specific keywords on the backend
		if (this.isTargetSpecialsRoot) {
			this.siteManagementService
				.fetchCurrentPageMetaTagSettings$()
				.pipe(takeUntil(this.destroyed$))
				.subscribe((metaTagSettingResponse) => {
					if (metaTagSettingResponse) {
						this.metaDataService.setPageMeta(
							metaTagSettingResponse.title ?? this.getPageTitle(),
							metaTagSettingResponse.title ?? this.getMetaTitle(),
							metaTagSettingResponse.description ?? this.getMetaDescription(),
							metaTagSettingResponse.image ?? this.getImage(),
							this.getUrl()
						);
					}
				});
		}
	}

	private setSideNavTopMargin(): void {
		if (
			this.piesHeaderRef?.nativeElement &&
			this.piesHeaderHeight !== this.piesHeaderRef.nativeElement.offsetHeight
		) {
			this.piesHeaderHeight = this.piesHeaderRef.nativeElement.offsetHeight;
			this.cdr.markForCheck();
		}
	}

	private setProductIndexOffset(): void {
		this.productIndexOffset$ = this.searchStoreService.state$.pipe(
			switchMap((state: SearchState) => {
				if (this.isInfiniteLoadMoreEnabled) {
					return of(0);
				}

				return of((state.currentPage - 1) * (state.currentPageSize || 24) || 0);
			})
		);
	}

	private setMaxBoughtBeforePosition(): void {
		this.searchStoreService.state$
			.pipe(takeUntil(this.destroyed$), withLatestFrom(this.boughtBeforeInSpecialsExperimentVariationKey$))
			.subscribe(([searchState, variantKey]) => {
				const conditionalMaxPosition = [
					{
						condition:
							variantKey === BoughtBeforeInSpcialsExperimentVariants.V2AllResultsOnFirstPage &&
							this.target === SearchTarget.Specials,
						maxPosition: searchState.currentPageSize ?? 24,
					},
					{
						condition:
							variantKey === BoughtBeforeInSpcialsExperimentVariants.V1FirstTwoResults &&
							this.target === SearchTarget.Specials,
						maxPosition: 2,
					},
					{
						condition: this.target && ALLOW_BOUGHT_BEFORE_ITEM_PAGES.includes(this.target),
						maxPosition: MAX_BOUGHT_BEFORE_ITEM_POSITION,
					},
				];

				this.maxBoughtBeforePosition =
					conditionalMaxPosition.find(({ condition }) => condition)?.maxPosition ??
					NO_BOUGHT_BEFORE_ITEM_POSITION;
			});
	}

	private updateSearchResultsOnFulfilmentChange(): void {
		this.fulfilmentStoreService.state$
			.pipe(
				distinctUntilFulfilmentChanged(),
				catchError((e) => {
					// eslint-disable-next-line no-console
					console.error(e);
					return throwError(() => e);
				}),
				skip(1),
				switchMap(() => this.searchService.rehydrateProductsFromApi()),
				takeUntil(this.destroyed$)
			)
			.subscribe(() => {
				this.dasFacetsQueryParams$.next(this.generateDasFacetsQueryParams());
				this.dasFacetsRoute$.next(this.generateDasFacetsRoute());

				if (this.shouldInitDynamicContent()) {
					setTimeout(() => this.initDynamicContent());
				}
			});
	}

	private isBrowseOrSpecialsAndEmbedded(): boolean {
		return (
			(this.target === SearchTarget.Browse || this.target === SearchTarget.Specials) &&
			this.appSettingsService.settings.isEmbeddedInApp
		);
	}
}
